import { useWorkspaceCloses } from "@api/close.api";
import { useSortedCloseStages } from "@api/organizationCloseStage.api";
import { getServiceUpdate } from "@components/tasks/taskClose";
import { CloseSelect } from "@components/tasks/TaskView/Cells/CloseSelectCell";
import { Picker } from "@design-system/Picker/Picker.lib";
import { useScope } from "@state/scopeContext.lib";
import type {
    RenderTaskChipTrigger,
    TaskFieldEditor,
} from "./TaskBoardFieldControls";
import type { TaskCardEditField } from "./taskCardEdits";

interface TaskCloseControlProps {
    editor: TaskFieldEditor;
    renderTrigger: RenderTaskChipTrigger;
}

/** List's Stage picker on a card trigger. */
export const TaskStageControl = ({
    editor: { task, onEdit },
    renderTrigger,
}: Readonly<TaskCloseControlProps>) => {
    const stages = useSortedCloseStages();
    return (
        <CloseSelect
            value={task.closeStageId ?? null}
            items={stages}
            nullLabel="No stage"
            onValueChange={(closeStageId) =>
                onEdit({ task, body: { closeStageId } })
            }
            trigger={<Picker.Trigger render={renderTrigger} />}
        />
    );
};

/** List's Service picker on a card trigger; clearing the service clears the stage too, as in List. */
export const TaskServiceControl = ({
    editor: { task, onEdit },
    renderTrigger,
}: Readonly<TaskCloseControlProps>) => {
    const { data: closes = [] } = useWorkspaceCloses();
    return (
        <CloseSelect
            value={task.closeId ?? null}
            items={closes}
            nullLabel="No service"
            onValueChange={(closeId) =>
                onEdit({ task, body: getServiceUpdate(closeId) })
            }
            trigger={<Picker.Trigger render={renderTrigger} />}
        />
    );
};

/**
 * The card fields the page can't edit: Stage and Service outside the
 * organization scope or before it has stages or services, where List shows
 * no cell for them.
 */
export const useUnofferedTaskFields = (): ReadonlySet<TaskCardEditField> => {
    const { organizationId } = useScope();
    const stages = useSortedCloseStages();
    const { data: closes = [] } = useWorkspaceCloses();
    const unofferedFields = new Set<TaskCardEditField>();
    if (!organizationId || stages.length === 0) {
        unofferedFields.add("stage");
    }
    if (!organizationId || closes.length === 0) {
        unofferedFields.add("service");
    }
    return unofferedFields;
};
